import React, { useCallback, useEffect, useState } from "react";

import {
  getAssistantConversation,
  listAssistantConversations,
} from "../../../api";
import SimpleMarkdown from "../../../components/SimpleMarkdown";
import { useNotify } from "../../../ToastContext";
import searchIconSrc from "../../../assets/search.svg";
// The transcript reuses the drawer's markdown styling (`gwAIMd`), including
// the table scroller, so a logged answer reads exactly as it did in the chat.
import "../../../styles/GatewayAIPopover.css";

const PAGE_SIZE = 25;
// Typing in the search box must not fire a request per keystroke; the list is
// a keyset query over an append-only log and the operator is usually pasting a
// name or a phrase they already know.
const SEARCH_DEBOUNCE_MS = 250;

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatArgs(args) {
  if (args === undefined || args === null) return "";
  if (typeof args === "string") return args;
  try {
    return JSON.stringify(args);
  } catch {
    return "";
  }
}

const VERDICT_LABEL = {
  approved: "Approved",
  rejected: "Cancelled",
  pending: "Never answered",
};

function ToolCallList({ calls }) {
  if (!Array.isArray(calls) || calls.length === 0) return null;
  return (
    <ul className="gwAhToolCalls">
      {calls.map((call, index) => (
        <li key={index} className={`gwAhToolCall${call.ok === false ? " isFailed" : ""}`}>
          <span className="gwAhToolName">{call.name}</span>
          {formatArgs(call.args) ? (
            <code className="gwAhToolArgs">{formatArgs(call.args)}</code>
          ) : null}
          <span className="gwAhToolResult">{call.error || call.result || ""}</span>
        </li>
      ))}
    </ul>
  );
}

// Read-only on purpose: this is a record of a decision already made, so it
// deliberately does NOT reuse the popover's approval card — a history page
// must never grow a live Approve button.
function ConfirmationList({ confirmations }) {
  if (!Array.isArray(confirmations) || confirmations.length === 0) return null;
  return (
    <div className="gwAhConfirmations">
      {confirmations.map((confirmation, index) => {
        const verdict = confirmation.verdict || "pending";
        return (
          <div key={index} className={`gwAhConfirmation is-${verdict}`}>
            <div className="gwAhConfirmationHead">
              <span className="gwAhVerdict">{VERDICT_LABEL[verdict] || verdict}</span>
              <span className="gwAhConfirmationTitle">
                {[confirmation.title, confirmation.target].filter(Boolean).join(" ") ||
                  confirmation.kind ||
                  "Action"}
              </span>
              {confirmation.kind ? (
                <code className="gwAhMutationKind">{confirmation.kind}</code>
              ) : null}
            </div>
            {confirmation.error ? (
              <div className="gwAhConfirmationError">{confirmation.error}</div>
            ) : confirmation.result ? (
              <div className="gwAhConfirmationResult">{confirmation.result}</div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function ConversationViewer({ conversation, onBack }) {
  const turns = Array.isArray(conversation.turns) ? conversation.turns : [];
  return (
    <div className="gwCardSection">
      <div className="gwSectionTitle gwAhViewerHead">
        <button type="button" className="gwSecondaryBtn" onClick={onBack}>
          Back to list
        </button>
        <span className="gwAhViewerTitle">{conversation.title || "(no first message)"}</span>
      </div>
      <div className="gwCardSectionBody">
        <div className="gwAhMeta">
          <span>{conversation.userName || conversation.userId || "unknown user"}</span>
          <span>{formatDateTime(conversation.startedAt)}</span>
          {conversation.provider || conversation.model ? (
            <span>{[conversation.provider, conversation.model].filter(Boolean).join(" · ")}</span>
          ) : null}
          <span>{conversation.turnCount} messages</span>
          <span>{conversation.mutationCount} approved mutations</span>
        </div>
        {conversation.error ? (
          <div className="gwAhConversationError">Last error: {conversation.error}</div>
        ) : null}
        {turns.length === 0 ? (
          <div className="gwAhEmpty">This conversation has no recorded messages.</div>
        ) : (
          <div className="gwAhTranscript">
            {turns.map((turn, index) => (
              <div
                key={index}
                className={`gwAhTurn ${turn.role === "user" ? "gwAhTurnUser" : "gwAhTurnAssistant"}`}
              >
                <div className="gwAhTurnRole">{turn.role === "user" ? "You" : "AI"}</div>
                <div className="gwAhTurnBody">
                  {turn.attachmentNames?.length ? (
                    <div className="gwAhAttachments">
                      {turn.attachmentNames.map((name, i) => (
                        <span key={i} className="gwAhAttachment">{name}</span>
                      ))}
                    </div>
                  ) : null}
                  {/* Same renderer as the drawer, so a table logged as
                      markdown reads as a table here too. */}
                  {turn.role === "assistant" ? (
                    <div className="gwAIMd">
                      <SimpleMarkdown text={turn.content} />
                    </div>
                  ) : (
                    <div className="gwAhUserText">{turn.content}</div>
                  )}
                  <ToolCallList calls={turn.toolCalls} />
                  <ConfirmationList confirmations={turn.confirmations} />
                  {turn.error ? (
                    <div className="gwAhTurnError">{turn.error}</div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function AssistantHistoryPage() {
  const notify = useNotify();
  const [conversations, setConversations] = useState([]);
  const [cursor, setCursor] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const page = await listAssistantConversations({ limit: PAGE_SIZE, q: search });
      setConversations(page.conversations || []);
      setCursor(page.nextCursor || "");
    } catch (err) {
      setLoadError(err.message || "Failed to load assistant history");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await listAssistantConversations({ limit: PAGE_SIZE, cursor, q: search });
      setConversations((prev) => [...prev, ...(page.conversations || [])]);
      setCursor(page.nextCursor || "");
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to load more" });
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, loadingMore, notify, search]);

  const openConversation = useCallback(
    async (id) => {
      try {
        setSelected(await getAssistantConversation(id));
      } catch (err) {
        notify({
          type: "error",
          title: "Error",
          message: err.message || "Failed to open conversation",
        });
      }
    },
    [notify]
  );

  if (selected) {
    return (
      <div className="gwContentGrid">
        <section className="gwFormStack">
          <div className="gwPageHeader">
            <div>
              <div className="gwPageTitle">Assistant History</div>
              <div className="gwPageSubtitle">
                Every conversation the AI Assistant has had on this gateway.
              </div>
            </div>
          </div>
          <ConversationViewer conversation={selected} onBack={() => setSelected(null)} />
        </section>
      </div>
    );
  }

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">Assistant History</div>
            <div className="gwPageSubtitle">
              Every conversation the AI Assistant has had on this gateway, with the
              tools it ran and the mutations that were approved. Nothing is deleted.
            </div>
          </div>
        </div>

        <div className="gwCardSection">
          <div className="gwSectionTitle gwAhListHead">
            <span>Conversations</span>
            <div className="gwSearch gwAhSearch">
              <img src={searchIconSrc} alt="" className="gwSearchIcon" />
              <input
                placeholder="Search first message or user…"
                aria-label="Search conversations"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </div>
          <div className="gwCardSectionBody gwAhListBody">
            {loading ? (
              <div className="gwAhEmpty">Loading…</div>
            ) : loadError ? (
              <div className="gwAhError" role="alert">
                {loadError}
              </div>
            ) : conversations.length === 0 ? (
              <div className="gwAhEmpty">
                {search
                  ? "No conversations match that search."
                  : "No assistant conversations recorded yet."}
              </div>
            ) : (
              conversations.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  className="gwAhRow"
                  onClick={() => openConversation(row.id)}
                >
                  <span className="gwAhRowTitle">{row.title || "(no first message)"}</span>
                  <span className="gwAhRowMeta">
                    <span className="gwAhRowUser">
                      {row.userName || row.userId || "unknown user"}
                    </span>
                    <span>{formatDateTime(row.startedAt)}</span>
                    <span>{row.turnCount} messages</span>
                    <span
                      className={row.mutationCount > 0 ? "gwAhRowMutations isActive" : "gwAhRowMutations"}
                    >
                      {row.mutationCount} approved mutations
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
          {cursor && !loading ? (
            <div className="gwAhLoadMore">
              <button
                type="button"
                className="gwSecondaryBtn"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}

export default AssistantHistoryPage;
